/**
 * The mockup's tab bar with the sliding ink — one per panel, because Part C invariant 2 says
 * each panel's tabs control only that panel and nothing else.
 *
 * The ink is a real element rather than a border so it can slide, and it is positioned from
 * the selected button's own offset. Where the browser has not laid the buttons out yet — a
 * test renderer, or the first paint — the ink simply has no width, which is invisible rather
 * than wrong; the selected tab is still marked by `aria-selected`, and the stylesheet gives a
 * solid background to the selected button when the ink cannot be measured.
 *
 * The competition tab bar tints its ink navy for Men's and pink for Women's (Part C
 * invariant 3), which is `tint` below. It is the competition colour and not a decoration: the
 * same two colours mark the competition everywhere else on this screen.
 */

import { useEffect, useRef } from "react";

export interface TabOption<T extends string> {
  value: T;
  label: string;
}

export interface TabsProps<T extends string> {
  label: string;
  options: readonly TabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** `competition` tints the ink with the competition colour; otherwise it stays ink-black. */
  tint?: "competition";
}

export function Tabs<T extends string>({
  label,
  options,
  value,
  onChange,
  tint,
}: TabsProps<T>): React.JSX.Element {
  const barRef = useRef<HTMLDivElement | null>(null);
  const inkRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const bar = barRef.current;
    const ink = inkRef.current;
    if (bar === null || ink === null) return;
    const selected = bar.querySelector<HTMLButtonElement>('[aria-selected="true"]');
    if (selected === null) return;
    ink.style.width = `${String(selected.offsetWidth)}px`;
    ink.style.transform = `translateX(${String(selected.offsetLeft)}px)`;
  }, [value, options]);

  const inkClass = [
    "tabs__ink",
    tint === "competition" && value === "m" ? "tabs__ink--men" : "",
    tint === "competition" && value === "w" ? "tabs__ink--women" : "",
  ]
    .filter((entry) => entry !== "")
    .join(" ");

  return (
    <div className="tabs" role="tablist" aria-label={label} ref={barRef}>
      <span className={inkClass} aria-hidden="true" ref={inkRef} />
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          onClick={() => {
            onChange(option.value);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** The three competition tabs, which every panel on this screen carries. */
export const COMPETITION_TABS = [
  { value: "all", label: "All" },
  { value: "m", label: "Men's" },
  { value: "w", label: "Women's" },
] as const;

/** The officials tabs of the three derived panels. */
export const ROLE_TABS = [
  { value: "all", label: "All" },
  { value: "TO", label: "TOs" },
  { value: "Judge", label: "Judges" },
] as const;

/** The Fixture List's stage tabs (§A3). */
export const STAGE_TABS = [
  { value: "all", label: "All" },
  { value: "pool", label: "Pool" },
  { value: "post", label: "Post-Pool" },
] as const;
